import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Landing.css';

// Marketing landing for Orchestrate — a pre-earnings equity-research workflow.
// "Quant Broadsheet" aesthetic, scoped entirely under .lp (see Landing.css).
// Public + chromeless (no app sidebar); CTAs route into the real auth flow.

const d = (s: number): React.CSSProperties => ({ ['--d' as string]: `${s}s` } as React.CSSProperties);

const TAPE = [
  ['NVDA', '+2.41%', 'up'], ['META', '+0.88%', 'up'], ['MSFT', '-0.32%', 'dn'],
  ['AMD', '+1.74%', 'up'], ['COST', '-0.11%', 'dn'], ['ADBE', '+0.59%', 'up'],
  ['AVGO', '+3.02%', 'up'], ['ORCL', '-1.06%', 'dn'], ['CRM', '+0.44%', 'up'],
  ['LULU', '-2.18%', 'dn'], ['MU', '+4.11%', 'up'], ['GOOGL', '+0.27%', 'up'],
] as const;

const STEPS = [
  ['01', 'Set-up', 'Read the tape into the print — positioning, short interest, the company profile.', 'Score'],
  ['02', 'Numbers', 'Your model vs. the Street. Where you sit, and exactly why.', 'Variance'],
  ['03', 'Valuation', 'NTM multiples, price target, the bull and bear, why it’s mispriced.', 'PT'],
  ['04', 'Industry', 'Tailwind, headwind, or neutral — and what customers are saying.', 'Read'],
  ['05', 'Outlook', 'What the call will reveal. Who’s the next buyer or seller.', 'Thesis'],
  ['06', 'Post-Print', 'Was the move rational? Where you want to be now.', 'React'],
  ['07', 'Action', 'The recommendation, the conviction, the next catalyst.', 'Verdict'],
];

// Landing-only display fonts (Fraunces + Hanken Grotesk + IBM Plex Mono).
// Loaded on-demand so they don't render-block every authenticated app page —
// only the /welcome route (this component) uses them. See Landing.css.
const LANDING_FONTS_ID = 'lp-display-fonts';
const LANDING_FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400..600;1,9..144,400..600&family=Hanken+Grotesk:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap';

const Landing: React.FC = () => {
  const root = useRef<HTMLDivElement>(null);

  // Inject the Landing display fonts on mount (idempotent via id guard).
  // Left in <head> on unmount so they stay cached for re-navigations; the
  // preconnect links in index.html still warm the Google Fonts connection.
  useEffect(() => {
    if (document.getElementById(LANDING_FONTS_ID)) return;
    const link = document.createElement('link');
    link.id = LANDING_FONTS_ID;
    link.rel = 'stylesheet';
    link.href = LANDING_FONTS_HREF;
    document.head.appendChild(link);
  }, []);

  // Scroll-reveal for below-the-fold sections.
  useEffect(() => {
    const el = root.current;
    if (!el) return;
    const targets = el.querySelectorAll('.scroll-reveal');
    const io = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } }),
      { threshold: 0.18, rootMargin: '0px 0px -8% 0px' },
    );
    targets.forEach(t => io.observe(t));
    return () => io.disconnect();
  }, []);

  return (
    <div className="lp" ref={root}>
      {/* NAV */}
      <nav className="nav">
        <span className="brand reveal" style={d(0)}><span className="mark" /> Orchestrate</span>
        <div className="nav-links">
          <a href="#method" className="hide-sm reveal" style={d(0.05)}>Method</a>
          <a href="#why" className="hide-sm reveal" style={d(0.1)}>Why</a>
          <Link to="/login" className="reveal" style={d(0.15)}>Sign in</Link>
          <Link to="/register" className="btn btn-fill reveal" style={d(0.2)}>
            <span>Start free</span>
          </Link>
        </div>
      </nav>

      {/* TICKER TAPE — decorative market texture, hidden from assistive tech */}
      <div className="tape reveal" style={d(0.25)} aria-hidden="true">
        <div className="tape-track">
          {[...TAPE, ...TAPE].map(([tk, mv, dir], i) => (
            <span className="tape-item" key={i}>
              <span className="tk">{tk}</span>
              <span className={dir === 'up' ? 'up' : 'dn'}>{mv}</span>
            </span>
          ))}
        </div>
      </div>

      {/* HERO */}
      <header className="hero wrap">
        <div className="ghost-num" aria-hidden>±</div>
        <div className="hero-grid">
          <div>
            <span className="eyebrow reveal" style={d(0.1)}>Pre-Earnings Research · Alpha Capture</span>
            <h1 className="reveal" style={d(0.18)}>
              Conviction,<br /><em>scored</em> before<br />the print.
            </h1>
            <p className="lede reveal" style={d(0.5)}>
              Orchestrate turns every earnings event into a disciplined, seven-step workup —
              <b> a rating and a reason at each step</b>, on live data, captured before the Street reacts.
            </p>
            <div className="cta-row reveal" style={d(0.62)}>
              <Link to="/register" className="btn btn-fill"><span>Begin a workup</span> <span className="arr">→</span></Link>
              <a href="#method" className="btn"><span>See the method</span></a>
            </div>
            <div className="meta reveal" style={d(0.74)}>
              <div className="stat"><b className="num">7</b><span>Steps / print</span></div>
              <div className="stat"><b className="num">5</b><span>−− to ++ scale</span></div>
              <div className="stat"><b className="num">0</b><span>Spreadsheets uploaded</span></div>
            </div>
          </div>

          {/* product scorecard object */}
          <div className="card reveal" style={d(0.55)}>
            <div className="c-top">
              <div>
                <div className="tkr">NVDA</div>
                <div className="co">NVIDIA Corp · Set-up</div>
              </div>
              <div className="prints">Prints in 3d</div>
            </div>

            <div className="c-label">Workup progress</div>
            <div className="rail">
              <span className="dot on" /><span className="seg on" />
              <span className="dot on" /><span className="seg on" />
              <span className="dot cur" /><span className="seg" />
              <span className="dot" /><span className="seg" />
              <span className="dot" /><span className="seg" />
              <span className="dot" /><span className="seg" />
              <span className="dot" />
            </div>

            <div className="c-label">Conviction</div>
            <div className="scale">
              <span className="s dd">−−</span>
              <span className="s d">−</span>
              <span className="s n">=</span>
              <span className="s u">+</span>
              <span className="s uu active">++</span>
            </div>

            <div className="lines">
              <div className="ln"><span>NTM P/E</span><span>31.2×</span></div>
              <div className="ln"><span>Cons. rev (corr.)</span><span>+0.78</span></div>
              <div className="ln"><span>Short interest</span><span>1.1%</span></div>
              <div className="ln"><span>Since pitch</span><span style={{ color: 'var(--up)' }}>+14.6%</span></div>
            </div>

            <div className="verdict">
              <span className="v">Very bullish</span>
              <span className="by">Analyst · scored</span>
            </div>
          </div>
        </div>
      </header>

      {/* METHOD — 7 steps */}
      <section className="band wrap" id="method">
        <div className="kicker scroll-reveal">
          <span className="idx">№ 01 — The Method</span>
          <h2>Seven verdicts, one disciplined sequence.</h2>
          <span className="line" />
        </div>
        <div className="steps scroll-reveal">
          {STEPS.map(([n, nm, ds, tag]) => (
            <div className="step" key={n}>
              <span className="n">{n}</span>
              <span className="nm">{nm}</span>
              <span className="ds">{ds}</span>
              <span className="tag">{tag}</span>
            </div>
          ))}
        </div>
      </section>

      {/* WHY — value props */}
      <section className="band wrap" id="why">
        <div className="kicker scroll-reveal">
          <span className="idx">№ 02 — Why Orchestrate</span>
          <h2>Built for the desk, not the deck.</h2>
          <span className="line" />
        </div>
        <div className="props scroll-reveal">
          <div className="prop">
            <div className="pn">i.</div>
            <h3>Score every step</h3>
            <p>A rating is mandatory at every step; the reasoning is optional. Your conviction becomes data you can grade against outcomes — print after print.</p>
            <div className="lab">Alpha capture</div>
          </div>
          <div className="prop">
            <div className="pn">ii.</div>
            <h3>Live data, zero uploads</h3>
            <p>Prices, consensus, the InSync model, comps and short interest stream in live. No Excel screenshots, no stale paste — the charts refresh while you work.</p>
            <div className="lab">FMP · InSync · FINRA</div>
          </div>
          <div className="prop">
            <div className="pn">iii.</div>
            <h3>Never miss a print</h3>
            <p>Earnings reminders fire at T-minus-10 and T-minus-3 to the analyst and the manager. Imminent prints surface on the home grid the moment they’re due.</p>
            <div className="lab">Reminders</div>
          </div>
        </div>
      </section>

      {/* MANIFESTO — the conviction scale */}
      <section className="manifesto scroll-reveal">
        <div className="wrap">
          <span className="eyebrow">№ 03 — The Scale</span>
          <h2>From <em>very bearish</em> to <em>very bullish</em> — five marks, no hedging.</h2>
          <div className="grade-strip">
            <span className="grade g1">−−<small>Very bearish</small></span>
            <span className="grade g2">−<small>Bearish</small></span>
            <span className="grade g3">=<small>Neutral</small></span>
            <span className="grade g4">+<small>Bullish</small></span>
            <span className="grade g5">++<small>Very bullish</small></span>
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="close wrap scroll-reveal">
        <h2>Score the print<br />before <em>the Street</em> does.</h2>
        <div className="cta-row">
          <Link to="/register" className="btn btn-fill"><span>Start a workup</span> <span className="arr">→</span></Link>
          <Link to="/login" className="btn"><span>Sign in</span></Link>
        </div>
      </section>

      {/* FOOTER */}
      <footer>
        <div className="wrap">
          <span className="fbrand">Orchestrate</span>
          <span className="fnote">
            A pre-earnings research workflow for equity analysts and the managers who cover them.
            Market data is illustrative. Not investment advice.
          </span>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
